import { useState, useMemo, useEffect } from "react";
import { useInfiniteQuery } from "@tanstack/react-query";
import { format, isToday, isYesterday, isThisWeek, isThisYear } from "date-fns";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Eye,
  BookOpen,
  Headphones,
  Clock,
  Search,
  Filter,
  Loader2,
  Calendar,
  Smartphone,
  Monitor,
  Globe,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useDebounce } from "@/hooks/useDebounce";
import { supabase } from "@/integrations/supabase/client";
import {
  fetchUserActivity,
  UserActivityEvent,
  ActivitySource,
} from "@/api/users-activity";

interface UserActivityFeedProps {
  userId: number;
  books: Array<{ id: number; title: string }>;
}

type ActivityFilter = "all" | "view" | "read" | "play";

const getActivityIcon = (source: ActivitySource) => {
  switch (source) {
    case "view":
      return <Eye className="h-4 w-4" />;
    case "read":
      return <BookOpen className="h-4 w-4" />;
    case "play":
      return <Headphones className="h-4 w-4" />;
  }
};

const getActivityColor = (source: ActivitySource) => {
  switch (source) {
    case "view":
      return "bg-blue-500/10 text-blue-500 border-blue-200";
    case "read":
      return "bg-green-500/10 text-green-500 border-green-200";
    case "play":
      return "bg-purple-500/10 text-purple-500 border-purple-200";
  }
};

const getPlatformIcon = (platform?: string | null) => {
  if (!platform) return null;

  switch (platform.toLowerCase()) {
    case "ios":
    case "android":
      return <Smartphone className="h-3 w-3" />;
    case "web":
      return <Globe className="h-3 w-3" />;
    default:
      return <Monitor className="h-3 w-3" />;
  }
};

const formatRelativeTime = (dateString: string) => {
  const date = new Date(dateString);

  if (isToday(date)) {
    return `Today at ${format(date, "h:mm a")}`;
  }

  if (isYesterday(date)) {
    return `Yesterday at ${format(date, "h:mm a")}`;
  }

  if (isThisWeek(date)) {
    return format(date, "EEEE 'at' h:mm a");
  }

  if (isThisYear(date)) {
    return format(date, "MMM dd 'at' h:mm a");
  }

  return format(date, "MMM dd, yyyy 'at' h:mm a");
};

const formatMinutesToReadableTime = (minutes: number): string => {
  if (!minutes || minutes <= 0) return "0m";
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  if (hours > 0 && mins > 0) return `${hours}h ${mins}m`;
  if (hours > 0) return `${hours}h`;
  return `${mins}m`;
};

const UserActivityFeed: React.FC<UserActivityFeedProps> = ({
  userId,
  books,
}) => {
  const [filter, setFilter] = useState<ActivityFilter>("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [dateRange, setDateRange] = useState<string>("all");
  const debouncedSearch = useDebounce(searchQuery, 600);
  const minSearchLength = 2;

  // Create books map for quick lookup
  const booksMap = useMemo(() => {
    return new Map(books.map((book) => [book.id, book.title]));
  }, [books]);

  // Dynamically fetched missing book titles cache
  const [extraBooksMap, setExtraBooksMap] = useState<Map<number, string>>(
    () => new Map()
  );

  // Combine static + dynamic maps
  const combinedBooksMap = useMemo(() => {
    const merged = new Map<number, string>(booksMap);
    extraBooksMap.forEach((title, id) => merged.set(id, title));
    return merged;
  }, [booksMap, extraBooksMap]);

  // Filter events based on current filters
  const filterEvents = (events: UserActivityEvent[]) => {
    let filtered = events;

    // Filter by activity type
    if (filter !== "all") {
      filtered = filtered.filter((event) => event.source === filter);
    }

    // Filter by search query (book title)
    if (debouncedSearch.trim().length >= minSearchLength) {
      const query = debouncedSearch.toLowerCase();
      filtered = filtered.filter((event) => {
        const bookTitle = booksMap.get(event.bookId)?.toLowerCase() || "";
        return bookTitle.includes(query);
      });
    }

    // Filter by date range
    if (dateRange !== "all") {
      const now = new Date();
      const cutoffDate = new Date();

      switch (dateRange) {
        case "today":
          cutoffDate.setHours(0, 0, 0, 0);
          break;
        case "week":
          cutoffDate.setDate(now.getDate() - 7);
          break;
        case "month":
          cutoffDate.setMonth(now.getMonth() - 1);
          break;
        case "year":
          cutoffDate.setFullYear(now.getFullYear() - 1);
          break;
      }

      filtered = filtered.filter(
        (event) => new Date(event.createdAt) >= cutoffDate
      );
    }

    return filtered;
  };

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    error,
  } = useInfiniteQuery({
    queryKey: ["userActivity", userId, filter, debouncedSearch, dateRange],
    queryFn: async ({ pageParam }) => {
      const events = await fetchUserActivity({
        userId,
        limit: 20,
        cursor: pageParam,
      });

      // Apply client-side filtering
      return filterEvents(events);
    },
    getNextPageParam: (lastPage) => {
      if (lastPage.length === 0) return undefined;
      return lastPage[lastPage.length - 1]?.createdAt;
    },
    initialPageParam: null as string | null,
  });

  const allEvents = useMemo(() => {
    return data?.pages.flat() || [];
  }, [data]);

  // Fetch missing book titles referenced by events
  useEffect(() => {
    const fetchMissing = async () => {
      if (!allEvents || allEvents.length === 0) return;
      const ids = Array.from(
        new Set(
          allEvents
            .map((e) => e.bookId)
            .filter((id) => typeof id === "number" && id > 0)
        )
      );

      const missing = ids.filter(
        (id) => !booksMap.has(id) && !extraBooksMap.has(id)
      );
      if (missing.length === 0) return;

      try {
        // Query in chunks to be safe
        const chunkSize = 50;
        const chunks: number[][] = [];
        for (let i = 0; i < missing.length; i += chunkSize) {
          chunks.push(missing.slice(i, i + chunkSize));
        }

        const results = await Promise.all(
          chunks.map((chunk) =>
            supabase.from("books").select("id, title").in("id", chunk)
          )
        );

        const newMap = new Map<number, string>(extraBooksMap);
        for (const res of results) {
          const rows = res.data || [];
          rows.forEach((row: { id: number; title: string }) => {
            newMap.set(row.id, row.title);
          });
        }
        setExtraBooksMap(newMap);
      } catch (e) {
        // Best-effort; ignore errors and keep Unknown Book fallback
        console.error("Failed to fetch missing book titles", e);
      }
    };

    fetchMissing();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allEvents, booksMap]);

  // Group events by date
  const groupedEvents = useMemo(() => {
    const groups: Record<string, UserActivityEvent[]> = {};

    allEvents.forEach((event) => {
      const date = new Date(event.createdAt).toDateString();
      if (!groups[date]) {
        groups[date] = [];
      }
      groups[date].push(event);
    });

    return groups;
  }, [allEvents]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-8">
        <p className="text-muted-foreground">
          Failed to load activity data. Please try again.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Filter className="h-5 w-5" />
            Activity Filters
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col sm:flex-row gap-4">
            {/* Activity Type Filter */}
            <div className="flex-1">
              <label className="text-sm font-medium mb-2 block">
                Activity Type
              </label>
              <Select
                value={filter}
                onValueChange={(value: ActivityFilter) => setFilter(value)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Activities</SelectItem>
                  <SelectItem value="view">Views Only</SelectItem>
                  <SelectItem value="read">Reads Only</SelectItem>
                  <SelectItem value="play">Audio Plays Only</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Search */}
            <div className="flex-1">
              <label className="text-sm font-medium mb-2 block">
                Search Books
              </label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search by book title..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>

            {/* Date Range */}
            <div className="flex-1">
              <label className="text-sm font-medium mb-2 block">
                Date Range
              </label>
              <Select value={dateRange} onValueChange={setDateRange}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Time</SelectItem>
                  <SelectItem value="today">Today</SelectItem>
                  <SelectItem value="week">Last 7 Days</SelectItem>
                  <SelectItem value="month">Last 30 Days</SelectItem>
                  <SelectItem value="year">Last Year</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Activity Feed */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calendar className="h-5 w-5" />
            Activity Timeline
            {allEvents.length > 0 && (
              <Badge variant="secondary" className="ml-2">
                {allEvents.length} events
              </Badge>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {allEvents.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-muted-foreground">
                No activity found matching your filters.
              </p>
            </div>
          ) : (
            <div className="space-y-6">
              {Object.entries(groupedEvents).map(([dateString, events]) => (
                <div key={dateString}>
                  {/* Date Header */}
                  <div className="flex items-center gap-2 mb-4">
                    <div className="h-px bg-border flex-1" />
                    <Badge variant="outline" className="px-3 py-1">
                      {format(new Date(dateString), "EEEE, MMM dd, yyyy")}
                    </Badge>
                    <div className="h-px bg-border flex-1" />
                  </div>

                  {/* Events for this date */}
                  <div className="space-y-3">
                    {events.map((event) => {
                      const bookTitle =
                        combinedBooksMap.get(event.bookId) || "Unknown Book";

                      return (
                        <div
                          key={event.id}
                          className="flex items-start gap-4 p-4 border rounded-lg hover:bg-muted/30 transition-colors"
                        >
                          {/* Activity Icon */}
                          <div
                            className={cn(
                              "flex items-center justify-center w-10 h-10 rounded-full border-2",
                              getActivityColor(event.source)
                            )}
                          >
                            {getActivityIcon(event.source)}
                          </div>

                          {/* Event Details */}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <h4 className="font-medium truncate">
                                {bookTitle}
                              </h4>
                              <Badge variant="outline" className="text-xs">
                                {event.source}
                              </Badge>
                            </div>

                            <div className="flex items-center gap-4 text-sm text-muted-foreground">
                              <span className="flex items-center gap-1">
                                <Clock className="h-3 w-3" />
                                {formatRelativeTime(event.createdAt)}
                              </span>

                              {event.minutes !== undefined && (
                                <span>
                                  Duration:{" "}
                                  {formatMinutesToReadableTime(event.minutes)}
                                </span>
                              )}

                              {event.source === "read" &&
                                event.completed !== undefined &&
                                (event.completed ? (
                                  <span className="inline-flex items-center gap-1 text-green-700 dark:text-green-300 bg-green-50 dark:bg-green-950/20 border border-green-200 dark:border-green-800 px-2 py-0.5 rounded text-xs">
                                    Completed
                                    {event.endedAt && (
                                      <>
                                        <span className="text-muted-foreground">
                                          •
                                        </span>
                                        {formatRelativeTime(event.endedAt)}
                                      </>
                                    )}
                                  </span>
                                ) : (
                                  <span className="inline-flex items-center gap-1 text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800 px-2 py-0.5 rounded text-xs">
                                    In progress
                                  </span>
                                ))}

                              {event.platform && (
                                <span className="flex items-center gap-1">
                                  {getPlatformIcon(event.platform)}
                                  {event.platform}
                                </span>
                              )}

                              {event.appVersion && (
                                <span>v{event.appVersion}</span>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}

              {/* Load More Button */}
              {hasNextPage && (
                <div className="flex justify-center pt-6">
                  <Button
                    onClick={() => fetchNextPage()}
                    disabled={isFetchingNextPage}
                    variant="outline"
                  >
                    {isFetchingNextPage ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Loading...
                      </>
                    ) : (
                      "Load More"
                    )}
                  </Button>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default UserActivityFeed;
